import { db } from '../firebase';
import { collection, addDoc, doc, updateDoc, getDoc, deleteDoc, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Save or update a forecast in Firestore
 */
export const saveForecastData = async (userId, forecastData, forecastId = null) => {
  try {
    const now = new Date().toISOString();
    
    // Prepare the forecast data for storage
    const forecastToSave = {
      userId: userId,
      updatedAt: now,
      status: forecastData.status || 'draft',
      
      // Structured inputs
      structuredData: {
        age: forecastData.age || null,
        incomeTrajectory: forecastData.incomeTrajectory || '',
        relationshipStatus: forecastData.relationshipStatus || '',
        weeklyExerciseHours: forecastData.weeklyExerciseHours || 0,
        sleepDebtHours: forecastData.sleepDebtHours || 0,
        biggestExpense: forecastData.biggestExpense || '',
        photoDescription: forecastData.photoDescription || '',
      },
      
      // Chat log - stored temporarily for analysis, deleted after processing
      chatLog: forecastData.chatLog || null,
      
      // Chat log metadata
      chatLogMetadata: {
        wordCount: forecastData.chatLogWordCount || 0,
        tokenCount: forecastData.chatLogTokenCount || 0,
        processedAt: forecastData.chatLogProcessedAt || null,
        compressionRatio: forecastData.chatLogCompressionRatio || null,
      },
      
      // AI analysis results (populated after analysis)
      aiAnalysis: forecastData.aiAnalysis || null,
      forecast: forecastData.forecast || null,
      
      // Payment tracking
      paymentStatus: forecastData.paymentStatus || null,
      paymentAmount: forecastData.paymentAmount || null,
      paymentDate: forecastData.paymentDate || null,
      paymentId: forecastData.paymentId || null,
      
      // PDF storage
      pdfUrl: forecastData.pdfUrl || null,
      pdfGeneratedAt: forecastData.pdfGeneratedAt || null,
      
      // Completion timestamps
      completedAt: forecastData.completedAt || null,
      analyzedAt: forecastData.analyzedAt || null,
    };

    // Only set createdAt for new forecasts
    if (!forecastId) {
      forecastToSave.createdAt = now;
    }

    // Save or update in Firestore
    let docRef;
    if (forecastId) {
      // Update existing forecast
      docRef = doc(db, 'forecasts', forecastId);
      await updateDoc(docRef, forecastToSave);
      return forecastId;
    } else {
      // Create new forecast
      docRef = await addDoc(collection(db, 'forecasts'), forecastToSave);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error saving forecast:', error);
    throw new Error(`Failed to save forecast: ${error.message}`);
  }
};

/**
 * Load a forecast by ID
 */
export const loadForecastById = async (forecastId) => {
  try {
    const docRef = doc(db, 'forecasts', forecastId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Forecast not found');
    }
  } catch (error) {
    console.error('Error loading forecast:', error);
    throw new Error(`Failed to load forecast: ${error.message}`);
  }
};

/**
 * Get all forecasts for a user
 */
export const getUserForecasts = async (userId) => {
  try {
    const q = query(
      collection(db, 'forecasts'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const forecasts = [];
    
    querySnapshot.forEach((doc) => {
      forecasts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by creation date (most recent first)
    forecasts.sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB - dateA;
    });
    
    return forecasts;
  } catch (error) {
    console.error('Error getting user forecasts:', error);
    throw new Error(`Failed to get forecasts: ${error.message}`);
  }
};

/**
 * Delete a forecast
 */
export const deleteForecast = async (forecastId) => {
  try {
    const docRef = doc(db, 'forecasts', forecastId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting forecast:', error);
    throw new Error(`Failed to delete forecast: ${error.message}`);
  }
};

/**
 * Call the Cloud Function to analyze a forecast
 */
export const analyzePatternLock = async (forecastId, forecastData, chatLogText) => {
  try {
    const functions = getFunctions();
    const analyzeFunction = httpsCallable(functions, 'analyzePatternLock');
    
    const result = await analyzeFunction({
      forecastId,
      age: forecastData.age,
      incomeTrajectory: forecastData.incomeTrajectory,
      relationshipStatus: forecastData.relationshipStatus,
      weeklyExerciseHours: forecastData.weeklyExerciseHours,
      sleepDebtHours: forecastData.sleepDebtHours,
      biggestExpense: forecastData.biggestExpense,
      photoDescription: forecastData.photoDescription,
      chatLog: chatLogText, // Optimized chat log text
    });
    
    if (!result.data.success) {
      throw new Error(result.data.error || 'Analysis failed');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error calling analyzePatternLock:', error);
    throw new Error(`Failed to analyze forecast: ${error.message}`);
  }
};

/**
 * Update forecast with payment information
 */
export const updateForecastPayment = async (forecastId, paymentData) => {
  try {
    const docRef = doc(db, 'forecasts', forecastId);
    await updateDoc(docRef, {
      paymentStatus: 'completed',
      paymentAmount: paymentData.amount,
      paymentDate: new Date().toISOString(),
      paymentId: paymentData.paymentIntentId,
      status: 'processing', // Move to processing after payment
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating forecast payment:', error);
    throw new Error(`Failed to update payment: ${error.message}`);
  }
};

/**
 * Update forecast with PDF URL
 */
export const updateForecastPDF = async (forecastId, pdfUrl) => {
  try {
    const docRef = doc(db, 'forecasts', forecastId);
    await updateDoc(docRef, {
      pdfUrl,
      pdfGeneratedAt: new Date().toISOString(),
      status: 'completed',
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating forecast PDF:', error);
    throw new Error(`Failed to update PDF: ${error.message}`);
  }
};

/**
 * Mark forecast as failed with error message
 */
export const markForecastFailed = async (forecastId, errorMessage) => {
  try {
    const docRef = doc(db, 'forecasts', forecastId);
    await updateDoc(docRef, {
      status: 'failed',
      error: errorMessage,
      failedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error marking forecast as failed:', error);
    throw new Error(`Failed to update forecast status: ${error.message}`);
  }
};

